import { Component, h, Prop } from '@stencil/core';
import { DateUtils, PersonDoc, SubmittalDoc, SubmittalRevision, TaskDoc, TaskRelationship } from 'common';
import { PeopleService } from 'src/global/service/PeopleService';
import { SpecificationSectionViewer } from '../../components/concourse-documents/specification-section-viewer';
import { TaskInfoModal } from '../../global/components/task-info-modal';

@Component({
  tag: 'submittal-details-card',
  styleUrls: ['../../global/styles/workflow-view.css'],
})
export class SubmittalDetailsCard {
  @Prop() submittal: SubmittalDoc;
  @Prop() people: PersonDoc[] = [];
  @Prop() scheduleTasks: TaskDoc[] = [];
  @Prop() selectedRevisionIndex: number = -1;
  @Prop() detailsExpanded: boolean = false;
  @Prop() descriptionTextExpanded: boolean = false;
  @Prop() canCreateRevision: boolean = false;
  @Prop() projectId: string;

  @Prop() removeTaskClick: (relationship: TaskRelationship) => void;
  @Prop() createRevision: () => void;
  @Prop() selectRevision: (index: number) => void;
  @Prop() toggleDescriptionText: () => void;

  get latestRevision(): SubmittalRevision {
    return this.submittal.revisions[this.submittal.revisions.length - 1];
  }

  get revision(): SubmittalRevision {
    if (this.selectedRevisionIndex === -1) {
      return this.latestRevision;
    }
    return this.submittal.revisions[this.selectedRevisionIndex];
  }

  getDescriptionText() {
    if (!this.submittal.description) return '';

    if (this.descriptionTextExpanded || this.submittal.description.length <= 100 || this.detailsExpanded) {
      return this.submittal.description;
    }

    return this.submittal.description.substring(0, 100) + '...';
  }

  async showSpecificationSectionViewer() {
    SpecificationSectionViewer.present(this.projectId, this.submittal.description, this.submittal.spec_section);
  }

  getRevisionList() {
    if (!this.submittal?.revisions || this.submittal.revisions.length === 0) return <div />;
    const selectedRevisionIndex = this.selectedRevisionIndex >= 0 ? this.selectedRevisionIndex : this.submittal.revisions.length - 1;
    return (
      <div>
        <ion-card-header class="ion-no-padding ion-padding-top">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <ion-card-title>Revisions</ion-card-title>
            {this.canCreateRevision && (
              <div>
                <ion-button data-testid="create-revision-button" color="warning" fill="clear" onClick={() => this.createRevision()}>
                  <ion-icon slot="icon-only" name="add-outline"></ion-icon>
                </ion-button>
              </div>
            )}
          </div>
        </ion-card-header>
        <div class="ion-no-padding">
          <div>
            {this.submittal.revisions.map((revision, index) => {
              const isSelected = selectedRevisionIndex === index;
              const currentState = this.getCurrentState();
              return (
                <div class={`revision-item ${isSelected ? 'selected' : ''} ${isSelected ? 'current' : ''}`} onClick={() => this.selectRevision(index)}>
                  <div class="revision-number">
                    <ion-icon
                      name={isSelected ? 'star' : 'star-outline'}
                      size="small"
                      style={{ color: isSelected ? 'var(--ion-color-light)' : 'var(--ion-color-step-400)' }}
                    ></ion-icon>
                    <div class="revision-number__content">
                      <ion-label class="typography-body2-500" style={{ color: isSelected ? 'var(--ion-color-light)' : 'var(--ion-color-step-400)' }}>
                        {revision.num == '0' ? 'Original' : `Rev ${revision.num}`} {isSelected && <small>(Current)</small>}
                      </ion-label>
                      {isSelected && (
                        <ion-label class="typography-caption" style={{ color: 'var(--ion-color-step-400)' }}>
                          {currentState && <span>{currentState.action}</span>} -{' '}
                          {revision.status_history?.length > 0 ? DateUtils.formatIso(revision.status_history[0].date) : 'Draft'}
                        </ion-label>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  getCurrentState() {
    const revision = this.revision;
    const statusHistory = revision?.status_history || [];
    if (statusHistory.length === 0) return null;
    const stateComplete = statusHistory[statusHistory.length - 1];
    return this.submittal.states.find(s => s.id === stateComplete.state_id);
  }

  render() {
    return (
      <div class="full-height ion-padding-horizontal vertical-scroll cc-scrollbars">
        <div class="workflow-view__details-container">
          <ion-list>
            <ion-item-group>
              <ion-item-divider>
                <ion-item-group>
                  <ion-card-header class="ion-no-padding">
                    <ion-card-title>Details</ion-card-title>
                  </ion-card-header>
                </ion-item-group>
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Title</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>{this.submittal.title ?? '-'}</ion-label>
                  </ion-item>
                </ion-item-group>
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Number</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>{this.submittal.number ?? '-'}</ion-label>
                  </ion-item>
                </ion-item-group>
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Due Date</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>{this.submittal.due_date ? DateUtils.formatIso(this.submittal.due_date) : '-'}</ion-label>
                  </ion-item>
                </ion-item-group>
              </ion-item-divider>

              <ion-item-divider>
                <ion-item-group>
                  <ion-card-header class="ion-no-padding ion-padding-top">
                    <ion-card-title>Additional Details</ion-card-title>
                  </ion-card-header>
                </ion-item-group>
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Type</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>{this.submittal.type ?? '-'}</ion-label>
                  </ion-item>
                </ion-item-group>
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Package</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>{this.submittal.package?.title ?? '-'}</ion-label>
                  </ion-item>
                </ion-item-group>
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Specification Section</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>
                      {this.submittal.spec_section ? (
                        <ion-button fill="clear" class="link-button" onClick={() => this.showSpecificationSectionViewer()}>
                          {this.submittal.spec_section.num}
                        </ion-button>
                      ) : (
                        <span>-</span>
                      )}
                    </ion-label>
                  </ion-item>
                </ion-item-group>
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Cost Code</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>{this.submittal.cost_code ? `${this.submittal.cost_code.code_number}` : '-'}</ion-label>
                  </ion-item>
                </ion-item-group>
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Description</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>
                      {this.getDescriptionText()}
                      {this.submittal.description && this.submittal.description.length > 100 && !this.detailsExpanded && (
                        <ion-button
                          fill="clear"
                          size="small"
                          onClick={() => this.toggleDescriptionText()}
                          class="link-button"
                          style={{ position: 'absolute', right: '0', bottom: '0' }}
                        >
                          {this.descriptionTextExpanded ? 'less' : 'more'}
                        </ion-button>
                      )}
                    </ion-label>
                  </ion-item>
                </ion-item-group>
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Affected Tasks</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>
                      {!this.submittal.successors || this.submittal.successors.length === 0
                        ? '-'
                        : this.submittal.successors.map(relationship => {
                            const taskId = relationship.task_unique_id;
                            const task = this.scheduleTasks && this.scheduleTasks.length > 0 ? this.scheduleTasks.find(t => t.unique_id == taskId) : null;
                            return (
                              <ion-chip onClick={() => TaskInfoModal.presentModal(task)}>
                                <ion-label>{task ? task.name : taskId}</ion-label>
                                <ion-icon
                                  name="close-circle"
                                  onClick={e => {
                                    e.stopPropagation();
                                    this.removeTaskClick(relationship);
                                  }}
                                ></ion-icon>
                              </ion-chip>
                            );
                          })}
                    </ion-label>
                  </ion-item>
                </ion-item-group>
                <ion-item-group>
                  <ion-item>
                    <ion-label>
                      <p class="typography-caption workflow-view__details-placeholder">Distribution</p>
                    </ion-label>
                  </ion-item>
                  <ion-item>
                    <ion-label>
                      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {this.submittal.distribution?.map(personId => {
                          const person = PeopleService.findPerson(this.people, personId);
                          return person ? <person-chip key={personId} person={person} /> : null;
                        })}
                      </div>
                    </ion-label>
                  </ion-item>
                </ion-item-group>
              </ion-item-divider>

              {this.getRevisionList()}
            </ion-item-group>
          </ion-list>
        </div>
      </div>
    );
  }
}
